import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { format } from "date-fns";

import { Actions as ALLOWANCEACTIONS } from "@/redux/actions/allowanceActions";
import { Actions as COMPANYACTIONS } from "@/redux/actions/companyActions";
import { Actions as FSSREPORTACTIONS } from "@/redux/actions/fssReportActions";
import { Actions as PAYROLL_ACTIONS } from "@/redux/actions/payrollActions";
import { Actions as PAYSLIPACTIONS } from "@/redux/actions/payslipActions";
import { Actions as PERMISSIONACTIONS } from "@/redux/actions/permissionActions";

import {
	BanknotesIcon,
	DocumentDuplicateIcon,
	EnvelopeIcon,
	ExclamationTriangleIcon,
	FolderIcon,
	NewspaperIcon,
	PencilSquareIcon,
	RectangleStackIcon,
} from "@heroicons/react/20/solid";
import { CurrencyEuroIcon, CurrencyPoundIcon } from "@/icons/outline";
import PayWithFyorin from "../../integrations/fyorin/PayWithFyorin";
import BankTransferModal from "../../payroll-history/bank-transfer-modal/BankTransferModal";
import PayrollSummary from "./PayrollSummary";

const Concluded = ({
	payPeriodDetails,
	setReopenPayrollModalOpen,
	generateRTISubmissions,
	downloadAllPayslips,
	downloadReport,
	openSendPayslipsModal,
	downloadFSSReport,
	jurisdiction,
	paymentIntegration,
	openBankTransferModal,
	openTestInLiveModal,
	showTILModal,
	clientReviewsPayroll,
	payrollGetPayPeriod,
	userIsAdvisor,
}) => {
	const CurrencyIcon = (props) => {
		switch (jurisdiction) {
			case "MT":
				return <CurrencyEuroIcon {...props} />;
			case "UK":
				return <CurrencyPoundIcon {...props} />;
		}
	};

	const bankTransferType = jurisdiction === "UK" ? "BACS" : "SEPA";

	const navigate = useNavigate();

	const { periodEnd, year, period } = payPeriodDetails || {};

	return (
		<div className="block text-center">
			<BankTransferModal />
			<div id="all-done">
				<div className="block-content block-content-full">
					<span className="item item-circle bg-success-light">
						<span className="item item-circle bg-primary">
							<i
								className="fas fa-gem text-white"
								style={{ width: "100%" }}
							/>
						</span>
					</span>
				</div>
				<div className="main">
					<h1 className="font-w700 text-center">Payroll Concluded.</h1>
					<p className="text-center">Seems like this payroll has been concluded.</p>
				</div>

				<PayrollSummary chartsOnly={true} />

				<div className="block-content">
					<div
						className="row"
						style={{ paddingBottom: 0 }}
					>
						<div className="col-sm-4">
							<div
								className="btn btn-default"
								type="button"
								onClick={() => downloadReport("payroll", payPeriodDetails.id)}
							>
								<DocumentDuplicateIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Download Payroll Report</div>
							</div>
						</div>

						<div className="col-sm-4">
							<div
								className="btn btn-default js-pdf-link"
								onClick={() => downloadAllPayslips(payPeriodDetails.id)}
							>
								<NewspaperIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Download Payslips</div>
							</div>
						</div>

						<div className="col-sm-4">
							<div className="btn-group" style={{ margin: "auto" }}>
								<button
									type="button"
									className="btn btn-default"
									onClick={() => {
										switch (jurisdiction) {
											case "UK":
												downloadFSSReport({
													year,
													name: "p30",
													month: period,
												});
												break;
											case "MT":
												downloadFSSReport({
													year,
													name: "fs5",
													month: periodEnd && format(new Date(periodEnd), "M"),
												});
												break;
										}
									}}
								>
									<PencilSquareIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
									<div style={{ float: "left" }}>
										Download{" "}
										{jurisdiction == "MT"
											? `${periodEnd && format(new Date(periodEnd), "MMMM")} FS5`
											: "P30"}
									</div>
								</button>
								{jurisdiction == "UK" && (
									<button
										type="button"
										className="btn btn-default"
										onClick={() => {
											downloadFSSReport({
												year,
												name: "p32",
												month: period,
											})
										}}
									>
										P32
									</button>
								)}
							</div>
						</div>
					</div>

					<div className="row">
						<div className="col-sm-4">
							<div
								className="btn btn-default"
								onClick={() => openSendPayslipsModal(payPeriodDetails.id)}
							>
								<EnvelopeIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Email Employees</div>
							</div>
						</div>

						<div className="col-sm-4">
							{!paymentIntegration?.name && (
								<div
									className="btn btn-default js-pdf-link"
									onClick={() => openBankTransferModal(payPeriodDetails)}
								>
									<RectangleStackIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
									<div style={{ float: "left" }}>Download {bankTransferType} File</div>
								</div>
							)}
							<PayWithFyorin
								period={payPeriodDetails}
								largeButton={true}
							/>
						</div>

						<div className="col-sm-4">
							{jurisdiction == "UK" && (
								<button
									className="btn btn-default"
									onClick={() => {
										if (showTILModal) {
											openTestInLiveModal();
										} else {
											generateRTISubmissions();
										}
									}}
								>
									<BanknotesIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
									<div style={{ float: "left" }}>Submit FPS</div>
								</button>
							)}
						</div>
					</div>
				</div>

				<div
					className="block-content"
					style={{ paddingBottom: 50 }}
				>
					<div className="col-sm-4">
						<a
							href="/organisation/payroll/current"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								payrollGetPayPeriod({ paySchedule: payPeriodDetails.paySchedule, navigate });
							}}
							className="btn btn-success"
						>
							<CurrencyIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
							<div style={{ float: "left" }}>Go to Current Payroll</div>
						</a>
					</div>
					<div className="col-sm-4">
						<Link
							to="/organisation/payroll-history"
							className="btn btn-default"
						>
							<FolderIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
							<div style={{ float: "left" }}>Payroll History</div>
						</Link>
					</div>

					<div className="col-sm-4">
						{(clientReviewsPayroll ? userIsAdvisor : true) && (
							<div
								className="btn btn-danger"
								onClick={() => setReopenPayrollModalOpen()}
							>
								<ExclamationTriangleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Reopen Payroll</div>
							</div>
						)}
					</div>
				</div>
				<div className="clearfix" />
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const payPeriodDetails = state?.payroll?.payPeriodDetails;

	const employees = state?.payroll?.employees?.filter((e) =>
		e.salaries.find(
			(s) =>
				!s.paySchedule ||
				(payPeriodDetails &&
					(s.paySchedule == payPeriodDetails.payPeriods || s.paySchedule == payPeriodDetails.paySchedule)),
		),
	);

	const { isAdmin } = state?.my?.profile || {};
	const clientReviewsPayroll = state?.company?.settings?.clientReviewsPayroll && state.company?.settings?.advisor;
	const userIsAdvisor = state?.my?.profile?.advisorId === state.company?.settings?.advisor || isAdmin;

	return {
		...state.payroll,
		payPeriodDetails,
		departmentList: state?.company?.settings?.departments,
		allowances: state?.allowances,
		employees,
		org: state?.company?.settings?.org,
		employeesNotReady:
			employees &&
			employees.filter(
				(employee) =>
					employee.employmentTypeName !== "External Contractor" &&
					!(
						employee.payslip?.ready &&
						(employee.payslip?.ready === true || employee.payslip?.ready?.toLowerCase?.() !== "no")
					),
			).length,
		totalEmployees:
			employees && employees.filter((employee) => employee.employmentTypeName !== "External Contractor").length,
		generating: state?.payroll?.generating?.all,
		permission: state?.permissions?.my?.payroll,
		jurisdiction: state?.company?.settings?.jurisdiction,
		paymentIntegration: state?.integration?.payment?.fyorin,
		showTILModal: state?.company?.settings?.showTILModal,
		clientReviewsPayroll,
		userIsAdvisor,
	};
};

const mapDispatchToProps = (dispatch) => ({
	payrollGetPayPeriod: (options) => dispatch(PAYROLL_ACTIONS.payrollGetPayPeriod(options)),
	getMyPermissions: () => dispatch(PERMISSIONACTIONS.getMyPermissions()),
	allowanceGetList: () => dispatch(ALLOWANCEACTIONS.allowanceGetList()),
	govtBonusGetList: (year) => dispatch(ALLOWANCEACTIONS.govtBonusGetList(year)),
	payrollGetList: (payrunID) => dispatch(PAYROLL_ACTIONS.payrollGetList(payrunID)),
	payrollSaveAll: () => dispatch(PAYROLL_ACTIONS.payrollSaveAll()),
	openSendPayslipsModal: (id) => dispatch(PAYSLIPACTIONS.sendPayslipsModal(true, id)),
	downloadAllPayslips: (id) => dispatch(PAYROLL_ACTIONS.downloadAllPayslips(id)),
	downloadReport: (reportType, payPeriods, year, period) =>
		dispatch(PAYROLL_ACTIONS.downloadReport(reportType, payPeriods, year, period)),
	setReopenPayrollModalOpen: () => dispatch(PAYROLL_ACTIONS.setReopenPayrollModalOpen(true)),
	generateRTISubmissions: () => dispatch(PAYROLL_ACTIONS.generateRTISubmissions()),
	downloadFSSReport: (data) => dispatch(FSSREPORTACTIONS.downloadFSSReport(data)),
	openBankTransferModal: (period) => {
		dispatch(COMPANYACTIONS.setBankTransferPeriod(period));
		dispatch(COMPANYACTIONS.setBankTransferModal(true));
	},
	openTestInLiveModal: () => {
		dispatch(PAYROLL_ACTIONS.setTestInLiveModal(true));
	},
});

export default connect(mapStateToProps, mapDispatchToProps)(Concluded);
